import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../api/initSupabase';
import { FontText, PrimaryButton, SEOHead } from '../components/common';
import { logErrorsWithMessage } from '../utils/errors';
import { localAnalytics } from '../utils/analytics';
import { Loading } from '../components/common/Loading';
import { DownloadButtonsBanner } from '../components/common/DownloadButtonsBanner';
import { handleLastChapterRedirection } from '../utils/navigation';
import { Header } from '../components/Header';
import { usePurchases } from '../contexts/PurchasesContext';
import { ReadingIcon } from '../components/common/ReadingIcon';

interface Book {
  id: number;
  title: string;
  picture: string | null;
  description: string | null;
  chapter_count: number;
  read_count: number;
  slug: string;
  author?: {
    name: string;
  };
  lastChapterRead?: number;
}

export const Home: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { userId, isAuthenticated, isCampaign, isAnonymous } = useAuth();
  const { isUserPremium, coinBalance } = usePurchases();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const fetchBooks = async () => {
    try {
      localAnalytics().logEvent('HomeDataLoading', {
        screen: 'Home',
        action: 'DataLoading',
        userId,
      });

      const booksResult = await supabase
        .from('book')
        .select(
          `
          *,
          book_author!inner(
            author:author_id(name)
          )
        `
        )
        .order('read_count', { ascending: false });

      if (booksResult.error) throw booksResult.error;

      let progressMap = new Map();

      if (isAuthenticated) {
        const [progressResult] = await Promise.all([
          supabase.rpc('get_books_progress'),
        ]);

        if (!progressResult.error) {
          progressMap = new Map(
            progressResult.data?.map((p: any) => [
              p.book_id,
              p.chapter_number,
            ]) || []
          );
        }
      }

      const booksWithProgress =
        booksResult.data?.map((book: any) => ({
          ...book,
          author: book.book_author[0]?.author,
          lastChapterRead: progressMap.get(book.id),
        })) || [];

      setBooks(booksWithProgress);

      localAnalytics().logEvent('HomeDataLoaded', {
        screen: 'Home',
        action: 'DataLoaded',
        userId,
        bookCount: booksWithProgress.length,
      });
    } catch (error) {
      logErrorsWithMessage(error, t('failed_to_load_books'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, [isAuthenticated]);

  useEffect(() => {
    if (userId && isAuthenticated) {
      handleLastChapterRedirection(userId, navigate);
    }
  }, [userId, isAuthenticated, navigate]);

  const handleBookPress = (book: Book) => {
    localAnalytics().logEvent('HomeBookClicked', {
      screen: 'Home',
      action: 'BookClicked',
      userId,
      bookId: book.id,
      bookTitle: book.title,
    });
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchBooks();
  };

  if (loading || refreshing) {
    return <Loading />;
  }

  const isLargeScreen = window.innerWidth > 900;

  const styles: Record<string, React.CSSProperties> = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 30px',
      borderBottom: `1px solid ${theme.colors.overlay}`,
    },
    title: {
      color: theme.colors.accent,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: 20,
      position: 'relative',
    },
    menuButton: {
      background: 'none',
      border: 'none',
      color: theme.colors.text,
      cursor: 'pointer',
      padding: 8,
    },
    main: {
      padding: 10,
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 400,
    },
    emptyText: {
      color: theme.colors.textSecondary,
      marginBottom: 20,
    },
    reloadButton: {
      padding: '12px 24px',
      backgroundColor: theme.colors.accent,
      color: theme.colors.white,
      border: 'none',
      borderRadius: 8,
      cursor: 'pointer',
    },
    booksGrid: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${isLargeScreen ? '250px' : '170px'}, 1fr))`,
      gap: 15,
    },
    bookCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
      textDecoration: 'none',
      display: 'block',
    },
    bookImage: {
      width: '100%',
      height: isLargeScreen ? 350 : 200,
      objectFit: 'cover',
      objectPosition: 'top',
    },
    bookInfo: {
      padding: 8,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      textAlign: 'center',
    },
    bookTitle: {
      color: theme.colors.text,
      marginBottom: 8,
    },
    bookAuthor: {
      color: theme.colors.textSecondary,
      marginBottom: 12,
    },
    bookStats: {
      marginTop: 10,
      display: 'flex',
      gap: 8,
    },
    bookStatsText: {
      color: theme.colors.textTertiary,
    },
    continueReading: {
      marginTop: 12,
      color: theme.colors.accentYellow,
    },
    downloadButtonsBanner: {
      margin: '0 -30px 0 -30px',
    },
    bookReading: {
      display: 'flex',
      flexDirection: 'row',
    },
    bookReadingIcon: {
      marginTop: -2,
    },
    heroSection: {
      textAlign: 'center',
      margin: '40px 0',
    },
    headline: {
      color: theme.colors.text,
      marginBottom: 12,
    },
    tagline: {
      color: theme.colors.textSecondary,
      marginBottom: 0,
    },
  };

  return (
    <>
      <SEOHead
        title={t('home_headline')}
        description={`${t('home_headline')} - ${t('home_tagline')} ${t('home_description')}`}
        keywords={`${t('home_keywords')}, billionaire romance, short stories`}
        canonical="/"
      />
      <div style={styles.container}>
        <Header
          isPremium={isUserPremium}
          isAuthenticated={isAuthenticated}
          isCampaign={isCampaign}
          isAnonymous={isAnonymous}
          userCoins={coinBalance}
          menuOpen={menuOpen}
          onMenuToggle={() => setMenuOpen(!menuOpen)}
        />

        <main style={styles.main}>
          <div style={styles.downloadButtonsBanner}>
            <DownloadButtonsBanner />
          </div>

          <div style={styles.heroSection}>
            <FontText h2 style={styles.headline}>
              {t('home_headline')}
            </FontText>
            <FontText h4 style={styles.tagline}>
              {t('home_tagline')}
            </FontText>
          </div>

          {books.length === 0 ? (
            <div style={styles.emptyState}>
              <FontText normal style={styles.emptyText}>
                {t('no_books_found')}
              </FontText>
              <PrimaryButton onClick={handleRefresh}>
                {t('reload')}
              </PrimaryButton>
            </div>
          ) : (
            <div style={styles.booksGrid}>
              {books.map(book => (
                <Link
                  key={book.id}
                  to={`/books/${book.slug}`}
                  style={styles.bookCard}
                  onClick={() => handleBookPress(book)}
                >
                  {book.picture && (
                    <img
                      src={book.picture}
                      alt={book.title}
                      style={styles.bookImage}
                    />
                  )}
                  <div style={styles.bookInfo}>
                    <FontText h4 style={styles.bookTitle}>
                      {book.title}
                    </FontText>
                    {book.author && (
                      <FontText small style={styles.bookAuthor}>
                        {t('by_author', { author: book.author.name })}
                      </FontText>
                    )}
                    <div style={styles.bookStats}>
                      <FontText tiny style={styles.bookStatsText}>
                        {t('chapter_count', { number: book.chapter_count })}
                      </FontText>
                      <FontText tiny style={styles.bookStatsText}>
                        |
                      </FontText>
                      <div style={styles.bookReading}>
                        <div style={styles.bookReadingIcon}>
                          <ReadingIcon />
                        </div>
                        <FontText tiny style={styles.bookStatsText}>
                          {book.read_count.toLocaleString()}
                        </FontText>
                      </div>
                    </div>
                    {book.lastChapterRead && isAuthenticated && (
                      <FontText small style={styles.continueReading}>
                        {t('continue_reading')}{' '}
                        {t('chapter_label', { number: book.lastChapterRead })}
                      </FontText>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
};
